import Icon from './Icon';

/**
 * UIButton — generic pill-style button primitive.
 *
 * Figma source: `uiButton` component-set `123:2482`, 14 inspected
 * variants (see design-system-manifest §5.3 table). This primitive
 * implements the 3 types × 3 sizes most used in app (primary, secondary,
 * tertiary) × (sm, md, lg); `accent` and `xs` variants flagged in
 * manifest §9 as not-individually-inspected and therefore not
 * implemented here (blocker: no inspected geometry).
 *
 * Variant geometry (all from manifest §5.4):
 *
 *   type=primary   fill: GRADIENT_LINEAR 552:6909→552:6796 diagonal (0,0)→(1,1)
 *                  text: text-inverse (#000)
 *
 *   type=secondary fill: surface-4 (rgba(255,255,255,0.16)) SOLID
 *                  backdropBlur: 24px
 *                  text: text-primary (#FFF)
 *
 *   type=tertiary  fill: base-white SOLID
 *                  text: text-inverse (#000)
 *
 *   size=sm        H=32 rounded-20 pad(8,8,13,13)   text-[12/14.088] icon=18
 *                  (figma 123:2481 / 318:4663: paddingL=R=8, icon 18×18)
 *   size=md        H=40 rounded-20 pad(24,24,13,13) text-[12/14.088] icon=16
 *   size=lg        H=48 rounded-24 pad(24,24,13,13) text-[14/16.436] icon=20
 *   (itemSpacing=6 for all sizes → gap-1.5)
 *
 * Used by:
 *  - BottomNav central "Создать": <UIButton type="primary" size="lg" leftIcon="coin">
 *  - Hero CTA "Попробовать":      <UIButton type="tertiary" size="sm" rightIcon="forward">
 *  - PresetGroupDetail "12 шаблонов": <UIButton type="secondary" size="sm" leftIcon="gallery">
 *
 * Props:
 *  - type   ('primary' | 'secondary' | 'tertiary')  default 'secondary'
 *  - size   ('sm' | 'md' | 'lg')                    default 'md'
 *  - leftIcon?  (icon name from /icons/)
 *  - rightIcon? (icon name from /icons/)
 *  - children (label)
 *  - onClick?
 *  - ...rest → <button>
 */

// Figma type variants → Tailwind classes (design-system-manifest §5.4)
const TYPE_CLASSES = {
  primary:
    'bg-gradient-to-br from-accent-orange-start to-accent-orange-end text-text-inverse',
  secondary:
    'bg-glass-16 backdrop-blur-secondary-btn text-text-primary',
  tertiary:
    'bg-base-white text-text-inverse',
};

// Figma size variants → Tailwind classes (height, radius, padding, text-size)
// Note: py-[13px] matches Figma pad-T/B=13 exactly (which is between
// Tailwind's py-3=12 and py-3.5=14). We use Tailwind's `py-3.25` equivalent
// via spacing token `3.25` → 13px declared in figma-tokens.json.
const SIZE_CLASSES = {
  sm: 'h-8  px-2 py-3.25 rounded-home-20 text-cta-sm',
  md: 'h-10 px-6 py-3.25 rounded-home-20 text-cta-sm',
  lg: 'h-12 px-6 py-3.25 rounded-home-24 text-cta-md-lg',
};

// Figma icon size inside button (manifest §5.4 + Figma 318:4663 instance
// component prop imageSize for sm-secondary = 18×18, not 12×12).
const SIZE_ICON = { sm: 18, md: 16, lg: 20 };

export default function UIButton({
  type = 'secondary',
  size = 'md',
  leftIcon,
  rightIcon,
  iconSize: iconSizeOverride,
  children,
  onClick,
  className = '',
  ...rest
}) {
  const cls = [
    // Base Figma auto-layout: HORIZONTAL + CENTER/CENTER + itemSpacing 6
    'inline-flex items-center justify-center gap-1.5 whitespace-nowrap',
    // Typography base (Raleway Bold set via fontSize token pair)
    'font-sans font-bold',
    TYPE_CLASSES[type] || TYPE_CLASSES.secondary,
    SIZE_CLASSES[size] || SIZE_CLASSES.md,
    className,
  ]
    .filter(Boolean)
    .join(' ');

  // iconSize: explicit prop overrides the size-derived default. Some Figma
  // sm-secondary instances ship 12×12 icons (e.g. /profile «История» 318:5926
  // has rightIcon=time at 12px while sm-default is 18px). Without this escape
  // hatch the consumer has no way to match Figma without touching the primitive.
  const iconSize = iconSizeOverride ?? (SIZE_ICON[size] || SIZE_ICON.md);

  return (
    <button type="button" onClick={onClick} className={cls} {...rest}>
      {leftIcon && <Icon name={leftIcon} size={iconSize} />}
      <span>{children}</span>
      {rightIcon && <Icon name={rightIcon} size={iconSize} />}
    </button>
  );
}
